<template>
  <header
    class="ecx-toolbar bg-white sticky top-0 z-[100] relative"
  >
    <!-- Linha horizontal que passa por baixo de tudo e se cruza com a sidebar -->
    <div class="absolute bottom-0 left-0 right-0 h-px bg-border-gray" />
    
    <div class="flex items-center justify-between px-4 py-2 h-14">
      <!-- Left Section -->
      <div class="flex items-center gap-x-3">
        <!-- Toggle Button -->
        <button
          v-if="showToggle"
          @click="handleToggle"
          :class="[
            'flex items-center justify-center w-10 h-10 transition-all duration-200',
            sidebarExpanded
              ? 'rounded-xl bg-royal-25 shadow-[inset_0px_2px_1px_0px_rgba(199,203,255,1.00)] outline outline-1 outline-offset-[-1px] outline-royal-50'
              : 'rounded-lg hover:bg-grey-50'
          ]"
          :aria-label="toggleLabel"
          :aria-expanded="sidebarExpanded"
        >
          <slot name="toggle-icon" :expanded="sidebarExpanded">
            <DrawerToggleIcon class="w-5 h-5" :class="sidebarExpanded ? 'text-primary' : 'text-subtitle-gray'" />
          </slot>
        </button>

        <!-- Left slot (breadcrumb, title, etc) -->
        <slot name="left" />
      </div>

      <!-- Center Section -->
      <div v-if="$slots.center" class="flex-1 flex items-center justify-center px-4">
        <slot name="center" />
      </div>

      <!-- Right Section -->
      <div class="flex items-center gap-x-2">
        <slot name="right" />

        <!-- User Profile -->
        <div v-if="showProfile" class="relative">
          <button
            @click="toggleProfileMenu"
            class="flex items-center gap-x-2 rounded-lg px-2 py-1.5 hover:bg-grey-50 transition-colors"
            :aria-expanded="isProfileOpen"
            aria-haspopup="true"
          >
            <!-- Avatar -->
            <div
              class="w-8 h-8 rounded-full overflow-hidden border-2 border-grey-200 flex items-center justify-center"
              :class="avatarUrl ? '' : 'bg-primary'"
            >
              <img
                v-if="avatarUrl"
                :src="avatarUrl"
                :alt="userName"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-white text-sm font-semibold">
                {{ userInitials }}
              </span>
            </div>
          </button>

          <!-- Profile Dropdown -->
          <Transition name="dropdown">
            <div
              v-if="isProfileOpen"
              class="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-border-gray py-2 z-50"
              @click.stop
            >
              <!-- User info -->
              <div class="px-4 py-3 border-b border-border-gray">
                <p class="font-medium text-default-black truncate">{{ userName }}</p>
                <p class="text-sm text-subtitle-gray truncate">{{ userEmail }}</p>
              </div>

              <!-- Menu items -->
              <div class="py-1">
                <slot name="profile-menu">
                  <button
                    class="w-full text-left px-4 py-2 text-sm text-subtitle-gray hover:bg-grey-50 transition-colors"
                    @click="handleSettings"
                  >
                    Configurações
                  </button>
                </slot>
              </div>

              <!-- Logout -->
              <div class="border-t border-border-gray pt-1">
                <button
                  class="w-full text-left px-4 py-2 text-sm text-error hover:bg-red-50 transition-colors"
                  @click="handleLogout"
                >
                  Sair
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import DrawerToggleIcon from '../../icons/DrawerToggleIcon.vue'

export interface ToolbarProps {
  /** Mostrar botão de toggle da sidebar */
  showToggle?: boolean
  /** Label do botão de toggle para acessibilidade */
  toggleLabel?: string
  /** Se a sidebar está expandida (para aria-expanded) */
  sidebarExpanded?: boolean
  /** Mostrar seção de perfil */
  showProfile?: boolean
  /** Nome do usuário */
  userName?: string
  /** Email do usuário */
  userEmail?: string
  /** URL do avatar (se não tiver, mostra iniciais) */
  avatarUrl?: string
}

const props = withDefaults(defineProps<ToolbarProps>(), {
  showToggle: true,
  toggleLabel: 'Toggle sidebar',
  sidebarExpanded: true,
  showProfile: true,
  userName: '',
  userEmail: '',
})

const emit = defineEmits<{
  (e: 'toggle'): void
  (e: 'logout'): void
  (e: 'settings'): void
}>()

// Profile menu state
const isProfileOpen = ref(false)

// Computed
const userInitials = computed(() => {
  if (!props.userName) return '?'
  const names = props.userName.trim().split(' ')
  if (names.length >= 2) {
    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
  }
  return props.userName.substring(0, 2).toUpperCase()
})

// Handlers
const handleToggle = () => {
  emit('toggle')
}

const toggleProfileMenu = () => {
  isProfileOpen.value = !isProfileOpen.value
}

const handleSettings = () => {
  isProfileOpen.value = false
  emit('settings')
}

const handleLogout = () => {
  isProfileOpen.value = false
  emit('logout')
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.relative')) {
    isProfileOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>

